import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnInit
} from '@angular/core';
import { POLYMORPHEUS_CONTEXT } from '@tinkoff/ng-polymorpheus';
import { TuiDialogContext } from '@taiga-ui/core';
import { IContractViewerDialogData } from '../../../interfaces/option-monitor.interface';
import {
  FormControl,
  FormGroup
} from '@angular/forms';
import {
  ISingleContractAnalyzerChartPoint,
  OptionTypeEnum,
  SingleContractAnalyzerType,
  SingleContractAnalyzerTypeEnum,
  TimeRangeEnum
} from 'app/charts-dashboard/interfaces';
import { Destroyer } from 'app/common/utils';
import {
  finalize,
  map,
  takeUntil
} from 'rxjs/operators';
import { locale } from 'app/option-monitor/components/dialogs/contract-viewer-dialog/locale';
import { ChartsHttpService } from 'app/charts-dashboard/services/charts-http.service';
import {
  BehaviorSubject,
  interval
} from 'rxjs';
import { isNumber } from '@datorama/akita';

@Component({
  selector: 'iv-live-contract-viewer-dialog',
  templateUrl: './contract-viewer-dialog.component.html',
  styleUrls: ['./contract-viewer-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush

})
export class ContractViewerDialogComponent extends Destroyer implements OnInit {

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private chartsDashboardService: ChartsHttpService,
    @Inject(POLYMORPHEUS_CONTEXT) public readonly context: TuiDialogContext<unknown, IContractViewerDialogData>
  ) {
    super();
  }

  public OptionTypeEnum = OptionTypeEnum;
  public optType = OptionTypeEnum.CALL
  public singleContractAnalyzerTypeEnum = SingleContractAnalyzerTypeEnum;
  public form: FormGroup = new FormGroup({
    mode: new FormControl(SingleContractAnalyzerTypeEnum.BID)
  });
  public params: SingleContractAnalyzerType | SingleContractAnalyzerTypeEnum[] = this.form.get('mode').value;
  public locale = locale;
  public $isLoading = new BehaviorSubject<boolean>(true);
  public optionData: ISingleContractAnalyzerChartPoint[];

  private tableFirstLoad = true;

  public ngOnInit(): void {
    this.formSubscription();
    this.loadOptionData();

    interval(20 * 1000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.loadOptionData());
  }

  public get tableTitlesColumns(): string[] {
    return this.tableColumns.map((param: SingleContractAnalyzerTypeEnum) => {
      switch (param) {
        case SingleContractAnalyzerTypeEnum.LAST_PRICE:
          return 'Last price';
        case SingleContractAnalyzerTypeEnum.TIME:
        case SingleContractAnalyzerTypeEnum.ASK:
        case SingleContractAnalyzerTypeEnum.BID:
        case SingleContractAnalyzerTypeEnum.VOLUME:
          return `${param.substring(0, 1).toUpperCase()}${param.substring(1, param.length)}`;
        case SingleContractAnalyzerTypeEnum.IV:
          return param.toUpperCase();
      }
    });
  }

  public get tableColumns(): string[] {
    return ['time','bid','ask','lastprice','iv','volume'];
  }

  public formSubscription(): void {
    this.form.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value: { mode: string }) => {
        if (value.mode === 'TABLE') {
          this.params = [
            SingleContractAnalyzerTypeEnum.LAST_PRICE,
            SingleContractAnalyzerTypeEnum.BID,
            SingleContractAnalyzerTypeEnum.ASK,
            SingleContractAnalyzerTypeEnum.IV,
            SingleContractAnalyzerTypeEnum.VOLUME
          ];
        } else {
          this.params = value.mode as SingleContractAnalyzerType;
          this.tableFirstLoad = true;
        }
        this.loadOptionData();
      });
  }

  private loadOptionData(): void {
    this.$isLoading.next(this.form.get('mode').value === 'TABLE' ? this.tableFirstLoad : true);

    this.chartsDashboardService
      .getContractView({
        stockId: this.context.data.instrument.stockId,
        symbol: this.context.data.instrument.symbol,
        timeRange: TimeRangeEnum.D1, // TODO: Первая версия графика
        expDate: this.context.data.expDate,
        strike: this.context.data.strike,
        optType: this.optType,
        params: (Array.isArray(this.params) ? `${this.params.join()}` : this.params) as SingleContractAnalyzerType,
        frequency: '1', // TODO: Первая версия графика
      })
      .pipe(
        map((response: ISingleContractAnalyzerChartPoint[]) => {
          let data = response;
          if (data[0] && data[0].time) {
            const lastDate: string = data.reduce((acc, cur) => (new Date(cur.time)).getTime() > (new Date(acc)).getTime() ? cur.time.toString() : acc, '0');
            data = data.filter((item: ISingleContractAnalyzerChartPoint) => item.time.toString().substring(0, 10) === lastDate.substring(0, 10));
          }
          return data.map((item: ISingleContractAnalyzerChartPoint) => {
            Object.keys(item).forEach((key: string) => {
              item[key] = isNumber(item[key])
                ? item[key] > 0
                  ? +(item[key] as number).toFixed(key === SingleContractAnalyzerTypeEnum.LAST_PRICE ? 3 : 2)
                  : 0
                : (item[key] as string).substring(11, 19);
            });
            return item;
          })
        }),
        finalize(() => this.$isLoading.next(false))
      )
      .subscribe((data: ISingleContractAnalyzerChartPoint[]) => {
        this.optionData = [...data];

        if (this.form.get('mode').value === 'TABLE') {
          this.tableFirstLoad = false;
        }
      });
  }

  private setOptType(optType: OptionTypeEnum): void {
    this.optType = optType
  }

   public changeOptTypeAndLoadData(optType: OptionTypeEnum): void {
    this.setOptType(optType)
    this.loadOptionData()
   }
}
